import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM, { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaPaperclip, FaTimes, FaEye, FaPlus, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import PdfPreviewModal from '../Reports/components/PdfPreviewModal';
import {
  generateSingleElementPdf,
  datedFileName,
} from '../../../../shared/pdf/pdfEngine';
import {
  addReceiptVoucher,
  getPostingAccounts,
  getBanksAndSafes,
  getCostCenters,
  addCostCenter,
  getProjects,
  addProject,
  getCustomers,
  addCustomer,
  getTaxes,
  addTax,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';

const createEmptyLine = () => ({
  id: Date.now() + Math.random(),
  account_id: '',
  amount: '',
  description: '',
  cost_center_id: '',
});

const AddReceiptVoucher = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n: i18nInstance } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const {
    postingAccounts,
    costCenters,
    banksAndSafes: banksAndSafesData,
    costCentersPagination,
    projects, projectsPagination,
    customers, customersPagination, // For project modal
    taxes, taxesPagination, // For project modal
    isLoading,
    success,
    error,
  } = useSelector((state) => state.auth);

  const [form, setForm] = useState({
    voucher_date: '',
    account_id: '',
    notes: '',
    reference: '',
    project_id: '',
    details: [createEmptyLine()],
  });

  const [windowWidth, setWindowWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const [attachments, setAttachments] = useState([]);
  const [mainDropdownOpen, setMainDropdownOpen] = useState(false);
  const [mainSearchTerm, setMainSearchTerm] = useState('');
  const mainSearchInputRef = useRef(null);
  // Keep selected "To" account for display when list lookup fails (e.g. ID type mismatch)
  const [selectedToAccount, setSelectedToAccount] = useState(null);

  const [detailDropdowns, setDetailDropdowns] = useState({});
  const [detailSearchTerms, setDetailSearchTerms] = useState({});
  const detailSearchInputRefs = useRef({});
  // Selected account per detail line (by line.id) for reliable display
  const [selectedDetailAccounts, setSelectedDetailAccounts] = useState({});
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [voucherForPrint, setVoucherForPrint] = useState(null);

  // Cost center dropdown states for each line
  const [costCenterDropdowns, setCostCenterDropdowns] = useState({});
  const [costCenterSearchTerms, setCostCenterSearchTerms] = useState({});
  const [costCenterCurrentPages, setCostCenterCurrentPages] = useState({});
  const costCenterSearchInputRefs = useRef({});
  const costCenterSearchTimeoutRefs = useRef({});
  // Selected cost center per detail line (by line.id) for reliable display
  const [selectedDetailCostCenters, setSelectedDetailCostCenters] = useState({});

  // Cost center modal state
  const [showAddCostCenterModal, setShowAddCostCenterModal] = useState(false);
  const [costCenterForm, setCostCenterForm] = useState({
    name_ar: '',
    name_en: '',
    code: '',
  });
  const [isSubmittingCostCenter, setIsSubmittingCostCenter] = useState(false);
  const [costCenterModalLineIndex, setCostCenterModalLineIndex] = useState(null);

  // Projects dropdown state
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectCurrentPage, setProjectCurrentPage] = useState(1);
  const projectSearchInputRef = useRef(null);
  const projectSearchTimeoutRef = useRef(null);

  // Project Modal Dropdowns State
  const [projModalCustomerDropdownOpen, setProjModalCustomerDropdownOpen] = useState(false);
  const [projModalCustomerSearchTerm, setProjModalCustomerSearchTerm] = useState('');
  const [projModalCustomerCurrentPage, setProjModalCustomerCurrentPage] = useState(1);
  const projModalCustomerSearchInputRef = useRef(null);
  const projModalCustomerSearchTimeoutRef = useRef(null);

  const [taxDropdownOpen, setTaxDropdownOpen] = useState(false);
  const [taxSearchTerm, setTaxSearchTerm] = useState('');
  const [taxCurrentPage, setTaxCurrentPage] = useState(1);
  const taxSearchInputRef = useRef(null);
  const taxSearchTimeoutRef = useRef(null);

  const [costCenterDropdownOpen, setCostCenterDropdownOpen] = useState(false);
  const [costCenterSearchTerm, setCostCenterSearchTerm] = useState('');
  const [costCenterCurrentPage, setCostCenterCurrentPage] = useState(1);
  const costCenterSearchInputRef = useRef(null);
  const costCenterSearchTimeoutRef = useRef(null);

  // Modals state
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [showAddTaxModal, setShowAddTaxModal] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  // Forms
  const [projectForm, setProjectForm] = useState({
    code: '',
    name_ar: '',
    name_en: '',
    customer_id: null,
    start_date: '',
    end_date: '',
    status: 'planned',
    contract_value: '',
    tax_id: null,
    cost_center_id: null,
    notes: '',
  });
  const [isSubmittingProject, setIsSubmittingProject] = useState(false);
  const projectNotesTextareaRef = useRef(null);

  const [taxForm, setTaxForm] = useState({
    name_ar: '',
    name_en: '',
    rate: '',
    is_active: true,
  });

  const [customerForm, setCustomerForm] = useState({
    name_en: '',
    name_ar: '',
    code: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    is_active: true,
    notes: ''
  });
  const [isSubmittingCustomer, setIsSubmittingCustomer] = useState(false);
  const [isSubOperation, setIsSubOperation] = useState(false);
  const [costCenterSource, setCostCenterSource] = useState(null); // 'line' or 'project'

  /* Effects */
  // Projects fetching
  useEffect(() => {
    if (projectDropdownOpen) {
      dispatch(getProjects({ page: projectCurrentPage, per_page: 10, search: projectSearchTerm || undefined }));
    }
  }, [dispatch, projectCurrentPage, projectDropdownOpen, projectSearchTerm]);

  // Customer fetching (for Project Modal)
  useEffect(() => {
    if (projModalCustomerDropdownOpen) {
      dispatch(getCustomers({ page: projModalCustomerCurrentPage, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
    }
  }, [dispatch, projModalCustomerCurrentPage, projModalCustomerDropdownOpen, projModalCustomerSearchTerm]);

  // Taxes fetching
  useEffect(() => {
    if (taxDropdownOpen) {
      dispatch(getTaxes({ page: taxCurrentPage, per_page: 10, search: taxSearchTerm || undefined }));
    }
  }, [dispatch, taxCurrentPage, taxDropdownOpen, taxSearchTerm]);

  // Cost Centers fetching (for Project Modal)
  useEffect(() => {
    if (costCenterDropdownOpen) {
      dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
    }
  }, [dispatch, costCenterCurrentPage, costCenterDropdownOpen, costCenterSearchTerm]);

  // Handlers
  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleTaxFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTaxForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleCustomerFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCustomerForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const renderProjectDisplay = (projectId) => {
    if (!projectId) return t('notesReceivable333.form.selectProject', { defaultValue: 'اختر المشروع' });
    const selected = (projects || []).find((p) => String(p.id) === String(projectId));
    return selected ? renderDropdownItemLabel(selected, 'code', 'name_ar', 'name_en') : t('notesReceivable333.form.selectProject', { defaultValue: 'اختر المشروع' });
  };

  const handleAddProject = async (e) => {
    e.preventDefault();
    if (!projectForm.name_ar) {
      toast.error(t('quotations.add.modals.addProject.requiredFields', { defaultValue: 'برجاء إدخال الاسم بالعربية' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingProject(true);
      const payload = {
        ...projectForm,
        contract_value: projectForm.contract_value ? Number(projectForm.contract_value) : undefined,
      };
      setIsSubOperation(true);
      const result = await dispatch(addProject(payload)).unwrap();
      dispatch(clearSuccess());
      dispatch(clearError());
      setForm((prev) => ({ ...prev, project_id: result.data.id }));
      await dispatch(getProjects({ page: 1, per_page: 10 }));
      setShowAddProjectModal(false);
      setProjectForm({
        code: '',
        name_ar: '',
        name_en: '',
        customer_id: null,
        start_date: '',
        end_date: '',
        status: 'planned',
        contract_value: '',
        tax_id: null,
        cost_center_id: null,
        notes: '',
      });
      toast.success(t('quotations.add.modals.addProject.success', { defaultValue: 'تم إضافة المشروع بنجاح' }), { rtl: isRTL });
    } catch (error) {
      dispatch(clearError());
      toast.error(error?.message || t('common.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingProject(false);
    }
  };

  const handleAddTax = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taxForm, rate: Number(taxForm.rate) };
      setIsSubOperation(true);
      const result = await dispatch(addTax(payload)).unwrap();
      if (result?.data?.id) {
        dispatch(clearSuccess());
        dispatch(clearError());
        setProjectForm(prev => ({ ...prev, tax_id: result.data.id }));
        await dispatch(getTaxes({ page: 1, per_page: 10 }));
        setShowAddTaxModal(false);
        setTaxForm({ name_ar: '', name_en: '', rate: '', is_active: true });
        toast.success(t('quotations.add.modals.addTax.success', { defaultValue: 'تم إضافة الضريبة بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      dispatch(clearError());
      toast.error(error?.message || t('common.error'), { rtl: isRTL });
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      setIsSubmittingCustomer(true);
      setIsSubOperation(true);
      const result = await dispatch(addCustomer({ ...customerForm, is_active: customerForm.is_active ? 1 : 0 })).unwrap();
      if (result) {
        dispatch(clearSuccess());
        dispatch(clearError());
        setProjectForm(prev => ({ ...prev, customer_id: result.data.id }));
        await dispatch(getCustomers({ page: 1, per_page: 10 }));
        setShowAddCustomerModal(false);
        setCustomerForm({
          name_en: '',
          name_ar: '',
          code: '',
          contact_person: '',
          email: '',
          phone: '',
          address: '',
          tax_number: '',
          is_active: true,
          notes: ''
        });
        toast.success(t('customers.add.success', { defaultValue: 'تم إضافة العميل بنجاح' }), { rtl: isRTL });
      }
    } catch (error) {
      dispatch(clearError());
      toast.error(error?.message || t('common.error'), { rtl: isRTL });
    } finally {
      setIsSubmittingCustomer(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('[data-dropdown-container]')) {
        setMainDropdownOpen(false);
        setProjectDropdownOpen(false);
        setDetailDropdowns({});
        setCostCenterDropdowns({});
        // Project modal dropdowns
        setProjModalCustomerDropdownOpen(false);
        setTaxDropdownOpen(false);
        setCostCenterDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window !== 'undefined') {
        setWindowWidth(window.innerWidth);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
    dispatch(getPostingAccounts());
    // Fetch large number to ensure we hit the cache for offline and have all items for dropdown
    dispatch(getCostCenters({ page: 1, per_page: 1000 }));
  }, [dispatch]);

  // Debounce search for cost centers
  useEffect(() => {
    Object.keys(costCenterSearchTerms).forEach((idx) => {
      const searchTerm = costCenterSearchTerms[idx];
      if (costCenterSearchTimeoutRefs.current[idx]) {
        clearTimeout(costCenterSearchTimeoutRefs.current[idx]);
      }
      costCenterSearchTimeoutRefs.current[idx] = setTimeout(() => {
        const page = costCenterCurrentPages[idx] || 1;
        setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: 1 }));
        dispatch(getCostCenters({ page: 1, per_page: 1000, search: searchTerm || undefined }));
      }, 300);
    });
    return () => {
      Object.values(costCenterSearchTimeoutRefs.current).forEach((timeout) => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, [costCenterSearchTerms, dispatch]);

  // Fetch cost centers when page changes
  useEffect(() => {
    Object.keys(costCenterCurrentPages).forEach((idx) => {
      const page = costCenterCurrentPages[idx] || 1;
      const search = costCenterSearchTerms[idx] || '';
      dispatch(getCostCenters({ page, per_page: 1000, search: search || undefined }));
    });
  }, [dispatch, costCenterCurrentPages]);

  // Fetch banks and safes on mount
  useEffect(() => {
    dispatch(getBanksAndSafes({ per_page: 1000 }));
  }, [dispatch]);

  // Keep selectedToAccount in sync with form.account_id (clear when "To" is cleared)
  useEffect(() => {
    if (!form.account_id) setSelectedToAccount(null);
  }, [form.account_id]);

  // Prune selectedDetailAccounts when lines are removed
  const detailLineIdsKey = useMemo(() => (form.details || []).map((d) => d.id).filter(Boolean).join(','), [form.details]);
  useEffect(() => {
    const ids = new Set((form.details || []).map((d) => d.id).filter(Boolean));
    setSelectedDetailAccounts((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => {
        if (ids.has(id)) next[id] = prev[id];
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
    setSelectedDetailCostCenters((prev) => {
      const next = {};
      Object.keys(prev).forEach((id) => {
        if (ids.has(id)) next[id] = prev[id];
      });
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [detailLineIdsKey]);

  useEffect(() => {
    if (success) {
      if (showAddCostCenterModal || showAddProjectModal || showAddTaxModal || showAddCustomerModal || isSubOperation) {
        dispatch(clearSuccess());
        setIsSubOperation(false);
        return;
      }
      toast.success(success, { rtl: true });
      dispatch(clearSuccess());
      setTimeout(() => {
        navigate('/receipt-vouchers');
      }, 1000);
    }
  }, [success, dispatch, navigate, showAddCostCenterModal, showAddProjectModal, showAddTaxModal, showAddCustomerModal, isSubOperation]);

  useEffect(() => {
    if (error) {
      if (showAddCostCenterModal || showAddProjectModal || showAddTaxModal || showAddCustomerModal || isSubOperation) {
        dispatch(clearError());
        setIsSubOperation(false);
        return;
      }
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      dispatch(clearError());
    }
  }, [error, dispatch, showAddCostCenterModal, showAddProjectModal, showAddTaxModal, showAddCustomerModal, isSubOperation]);

  const totalAmount = useMemo(
    () =>
      form.details.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0),
    [form.details]
  );

  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      const locale = 'en-US';
      return date.toLocaleDateString(locale, { year: 'numeric', month: '2-digit', day: '2-digit' });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '0.00';
    const locale = 'en-US';
    return parseFloat(amount).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const statusLabel = (status) => {
    return t(`receiptVouchers.status.${status || 'draft'}`, { defaultValue: status || '—' });
  };

  // Create preview voucher object from form data
  const createPreviewVoucher = () => {
    const selectedAccount = banksAndSafes.find(acc => String(acc.id) === String(form.account_id));
    const details = form.details
      .filter(line => line.account_id && line.amount)
      .map(line => {
        const account = (postingAccounts || []).find(acc => String(acc.id) === String(line.account_id));
        return {
          id: line.id,
          account_id: line.account_id,
          amount: parseFloat(line.amount) || 0,
          description: line.description || '',
          account: account || null
        };
      });

    return {
      id: 'preview',
      voucher_date: form.voucher_date,
      reference: form.reference || '',
      notes: form.notes || '',
      total_amount: totalAmount,
      status: 'draft',
      account: selectedAccount || null,
      details: details
    };
  };

  const handlePreviewPDF = () => {
    if (!form.voucher_date || !form.account_id || totalAmount <= 0) {
      toast.error(t('receiptVouchers.add.errors.detailsRequired', { defaultValue: 'Please fill required fields first' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }
    setIsPreviewOpen(true);
  };

  const handlePrintFromPreview = async () => {
    setIsGeneratingPdf(true);
    try {
      const previewVoucher = createPreviewVoucher();
      setVoucherForPrint(previewVoucher);

      // Wait for template to render
      await new Promise(resolve => setTimeout(resolve, 500));

      const element = document.getElementById(`receipt-voucher-pdf-${previewVoucher.id}`);
      if (!element) {
        throw new Error('Print template not found');
      }

      // Shared PDF Engine — capture + assembly
      await generateSingleElementPdf({
        elementId: element.id,
        fileName: datedFileName(
          t('receiptVouchers.excel.fileName', { defaultValue: 'ReceiptVoucher' }),
        ),
        isRTL,
      });

      toast.success(t('receiptVouchers.print.success', { defaultValue: 'PDF downloaded successfully' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } catch (error) {
      toast.error(error.message || t('receiptVouchers.print.error', { defaultValue: 'An error occurred while creating the PDF' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // Use banks and safes from the combined endpoint
  const banksAndSafes = useMemo(() => {
    return Array.isArray(banksAndSafesData) ? banksAndSafesData : [];
  }, [banksAndSafesData]);

  const filteredBanksAndSafes = useMemo(() => {
    const term = (mainSearchTerm || '').toLowerCase().trim();
    if (!term) return banksAndSafes;
    return banksAndSafes.filter((acc) => {
      const code = (acc.code || '').toLowerCase();
      const nameAr = (acc.name_ar || '').toLowerCase();
      const nameEn = (acc.name_en || acc.name || '').toLowerCase();
      const name = isRTL ? nameAr : nameEn;
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  }, [banksAndSafes, mainSearchTerm, isRTL]);

  const getFilteredAccountsForLine = (idx) => {
    const term = (detailSearchTerms[idx] || '').toLowerCase().trim();
    if (!term) return postingAccounts || [];
    return (postingAccounts || []).filter((acc) => {
      const code = (acc.code || '').toLowerCase();
      const nameAr = (acc.name_ar || '').toLowerCase();
      const nameEn = (acc.name_en || acc.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const mapped = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      url: URL.createObjectURL(file),
    }));

    setAttachments((prev) => [...prev, ...mapped]);
    event.target.value = '';
  };

  const removeAttachment = (id) => {
    setAttachments((prev) => {
      const target = prev.find((att) => att.id === id);
      if (target?.url) {
        URL.revokeObjectURL(target.url);
      }
      return prev.filter((att) => att.id !== id);
    });
  };

  const updateLine = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      details: prev.details.map((line, lineIdx) =>
        lineIdx === idx ? { ...line, [field]: value } : line
      ),
    }));
  };

  const addDetailLine = () => {
    setForm((prev) => ({
      ...prev,
      details: [...prev.details, createEmptyLine()],
    }));
  };

  const removeDetailLine = (idx) => {
    setForm((prev) => {
      if (prev.details.length === 1) {
        return prev;
      }
      return {
        ...prev,
        details: prev.details.filter((_, lineIdx) => lineIdx !== idx),
      };
    });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.voucher_date) {
      toast.error('يرجى تحديد تاريخ الإيصال', { rtl: true });
      return;
    }

    if (!form.account_id) {
      toast.error('يرجى اختيار حساب الخزينة أو البنك المستلم', { rtl: true });
      return;
    }

    const detailsArray = form.details
      .map((line) => ({
        account_id: Number(line.account_id),
        amount: parseFloat(line.amount) || 0,
        description: line.description || '',
        cost_center_id: line.cost_center_id ? Number(line.cost_center_id) : null,
      }))
      .filter((line) => line.account_id && line.amount > 0);

    if (detailsArray.length === 0) {
      toast.error(t('receiptVouchers.add.errors.detailsRequired', { defaultValue: 'Please add at least one detail line with account and valid amount' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    const total = detailsArray.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0);
    if (total <= 0) {
      toast.error(t('receiptVouchers.add.errors.totalRequired', { defaultValue: 'Total amount must be greater than zero' }), {
        rtl: isRTL,
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true
      });
      return;
    }

    const payloadBase = {
      voucher_date: form.voucher_date,
      account_id: Number(form.account_id),
      total_amount: Number(total.toFixed(2)),
      notes: form.notes || '',
      reference: form.reference || '',
      details: detailsArray,
      project_id: form.project_id ? Number(form.project_id) : null,
    };

    if (attachments.length > 0) {
      const formData = new FormData();
      formData.append('voucher_date', payloadBase.voucher_date);
      formData.append('account_id', payloadBase.account_id);
      formData.append('total_amount', payloadBase.total_amount);
      formData.append('notes', payloadBase.notes);
      if (payloadBase.reference) {
        formData.append('reference', payloadBase.reference);
      }
      formData.append('details', JSON.stringify(detailsArray));

      attachments.forEach((attachment) => {
        formData.append('attachments[]', attachment.file);
      });

      if (payloadBase.project_id) {
        formData.append('project_id', payloadBase.project_id);
      }

      dispatch(addReceiptVoucher(formData));
    } else {
      dispatch(addReceiptVoucher(payloadBase));
    }
  };

  // Swap logic: isFromAccount now means 'to' and not 'from'
  const renderAccountDisplay = (accountId, isToAccount = false) => {
    if (!accountId) return isToAccount
      ? t('receiptVouchers.add.selectTo', { defaultValue: 'Select To' })
      : t('receiptVouchers.add.selectAccount', { defaultValue: 'Select Account' });

    if (isToAccount) {
      const selected = banksAndSafes.find(
        (acc) => String(acc.id) === String(accountId) || String(acc.account_id) === String(accountId)
      );
      if (!selected) return t('receiptVouchers.add.selectTo', { defaultValue: 'Select To' });
      const accountName = isRTL ? (selected.name_ar || selected.name_en || selected.name) : (selected.name_en || selected.name_ar || selected.name);
      return accountName || '';
    } else {
      const selected = (postingAccounts || []).find(
        (acc) => String(acc.id) === String(accountId)
      );
      if (!selected) return t('receiptVouchers.add.selectAccount', { defaultValue: 'Select Account' });
      const accountName = isRTL ? (selected.name_ar || selected.name_en || selected.name) : (selected.name_en || selected.name_ar || selected.name);
      return accountName || '';
    }
  };

  const isTablet = windowWidth <= 992;
  const isMobile = windowWidth <= 768;

  const costCenterOptions = useMemo(() => {
    if (!Array.isArray(costCenters)) return [];
    return costCenters;
  }, [costCenters]);

  const getFilteredCostCentersForLine = (idx) => {
    const term = (costCenterSearchTerms[idx] || '').toLowerCase().trim();
    if (!term) return costCenterOptions;
    return costCenterOptions.filter((cc) => {
      const code = (cc.code || '').toLowerCase();
      const nameAr = (cc.name_ar || '').toLowerCase();
      const nameEn = (cc.name_en || cc.name || '').toLowerCase();
      return code.includes(term) || nameAr.includes(term) || nameEn.includes(term);
    });
  };

  const renderCostCenterDisplay = (costCenterId) => {
    if (!costCenterId) return t('paymentVouchers.form.noCostCenter', { defaultValue: 'No Cost Center' });
    const selected = (costCenters || []).find((cc) => String(cc.id) === String(costCenterId));
    if (!selected) return t('paymentVouchers.form.noCostCenter', { defaultValue: 'No Cost Center' });
    const costCenterName = isRTL ? (selected.name_ar || selected.name || '') : (selected.name_en || selected.name || selected.name_ar || '');
    return `${selected.code ? `${selected.code} - ` : ''}${costCenterName}`;
  };

  const handleCostCenterFormChange = (e) => {
    const { name, value } = e.target;
    setCostCenterForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddCostCenter = async (e) => {
    e.preventDefault();
    if (!costCenterForm.name_ar) {
      toast.error(t('paymentVouchers.form.modals.addCostCenter.requiredFields', { defaultValue: 'Required Fields Missing' }), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmittingCostCenter(true);
      setIsSubOperation(true); // Ensure flag is set
      const payload = {
        name_ar: costCenterForm.name_ar,
        name_en: costCenterForm.name_en || undefined,
        code: costCenterForm.code || undefined,
      };
      const result = await dispatch(addCostCenter(payload)).unwrap();
      if (result?.data?.id) {
        dispatch(clearSuccess());
        dispatch(clearError());

        // Handle source
        if (costCenterSource === 'line' && costCenterModalLineIndex !== null) {
          updateLine(costCenterModalLineIndex, 'cost_center_id', result.data.id);
        } else if (costCenterSource === 'project') {
          setProjectForm(prev => ({ ...prev, cost_center_id: result.data.id }));
        }

        // Refresh cost centers list
        // If from project, we might need to refresh the cost center list used in project modal if it had its own pagination/list, 
        // but currently it shares the global costCenters list which is refreshed below.
        Object.keys(costCenterCurrentPages).forEach((idx) => {
          const page = costCenterCurrentPages[idx] || 1;
          const search = costCenterSearchTerms[idx] || '';
          dispatch(getCostCenters({ page, per_page: 10, search: search || undefined }));
        });

        // Also refresh general list if needed (e.g. for Project Modal dropdown if useMemo is not enough or if it uses separate fetch)
        // Project Modal uses 'costCenters' from state, which is updated by any getCostCenters call usually. 
        // The above loop updates per-line dropdowns.
        // We should also ensure the project modal's dropdown list is updated if it uses a specific search/page.
        if (costCenterDropdownOpen) {
          dispatch(getCostCenters({ page: costCenterCurrentPage, per_page: 10, search: costCenterSearchTerm || undefined }));
        } else {
          // Just fetch recent/all to ensure it's in the list
          dispatch(getCostCenters({ page: 1, per_page: 1000 }));
        }

        setShowAddCostCenterModal(false);
        setCostCenterForm({
          name_ar: '',
          name_en: '',
          code: '',
        });
        setCostCenterModalLineIndex(null);
        toast.success(t('paymentVouchers.form.modals.addCostCenter.success', { defaultValue: 'Cost Center Added' }), { rtl: isRTL });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || t('paymentVouchers.form.modals.addCostCenter.error', { defaultValue: 'Error adding Cost Center' }), { rtl: isRTL });
    } finally {
      setIsSubmittingCostCenter(false);
    }
  };

  return (
    <div
      style={{
        padding: isMobile ? '16px' : '30px',
        backgroundColor: '#14263b',
        minHeight: 'calc(100vh - 80px)',
        color: 'white',
        boxSizing: 'border-box',
        direction: dir,
        textAlign: textAlign
      }}
    >
      <h1 style={{ fontSize: isMobile ? '18px' : '20px', marginBottom: isMobile ? '12px' : '16px', textAlign: textAlign, direction: dir }}>
        {t('receiptVouchers.add.title', { defaultValue: 'Add Receipt Voucher' })}
      </h1>
      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#1b3350',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: isMobile ? '16px' : '20px',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <div>
            <label style={{ display: 'block', marginBottom: 6, textAlign: textAlign, direction: dir }}>{t('receiptVouchers.add.date', { defaultValue: 'Date' })}</label>
            <input
              type="date"
              value={form.voucher_date}
              onChange={(e) => setForm((prev) => ({ ...prev, voucher_date: e.target.value }))}
              required
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                color: 'white',
                borderRadius: 8,
                padding: '10px',
                direction: dir
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, textAlign: textAlign, direction: dir }}>{t('receiptVouchers.add.reference', { defaultValue: 'Reference' })}</label>
            <input
              type="text"
              value={form.reference}
              onChange={(e) => setForm((prev) => ({ ...prev, reference: e.target.value }))}
              style={{
                width: '100%',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                color: 'white',
                borderRadius: 8,
                padding: '10px',
                direction: dir
              }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: 6, textAlign: textAlign, direction: dir }}>{t('receiptVouchers.add.to', { defaultValue: 'To' })}</label>
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => {
                setMainDropdownOpen((prev) => !prev);
                setTimeout(() => {
                  if (mainSearchInputRef.current) {
                    mainSearchInputRef.current.focus();
                  }
                }, 100);
              }}
              style={{
                width: '100%',
                padding: '10px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxSizing: 'border-box',
              }}
            >
              <span>
                {selectedToAccount && (String(selectedToAccount.id) === String(form.account_id) || String(selectedToAccount.account_id) === String(form.account_id))
                  ? (isRTL ? (selectedToAccount.name_ar || selectedToAccount.name_en || selectedToAccount.name) : (selectedToAccount.name_en || selectedToAccount.name_ar || selectedToAccount.name))
                  : renderAccountDisplay(form.account_id, true)}
              </span>
              <span style={{ fontSize: '12px' }}>▼</span>
            </div>
            {mainDropdownOpen && (
              <div
                style={{
                  position: 'fixed',
                  inset: 0,
                  zIndex: 998,
                  background: 'transparent',
                }}
                aria-hidden="true"
                onClick={() => setMainDropdownOpen(false)}
              />
            )}
            {mainDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '0 0 8px 8px',
                  zIndex: 1000,
                  maxHeight: '300px',
                  overflowY: 'auto',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  ref={mainSearchInputRef}
                  type="text"
                  placeholder={t('receiptVouchers.add.searchAccount', { defaultValue: 'Search for account...' })}
                  value={mainSearchTerm}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMainSearchTerm(value);
                    // Search is handled by filteredBanksAndSafes useMemo
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#2d3748',
                    border: 'none',
                    borderBottom: '1px solid #333',
                    color: 'white',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    direction: dir
                  }}
                  dir={dir}
                />
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  {filteredBanksAndSafes.length > 0 ? (
                    filteredBanksAndSafes.map((acc) => {
                      
                      const accId = acc.id ?? acc.account_id;
                      const isSelected = String(form.account_id) === String(accId);
                      return (
                        <div
                          key={acc.id ?? acc.account_id ?? accId}
                          role="option"
                          aria-selected={isSelected}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (isSelected) return;
                            const accountId = acc.id ?? acc.account_id;
                            setForm((prev) => ({ ...prev, account_id: accountId }));
                            setSelectedToAccount(acc);
                            setMainDropdownOpen(false);
                            setMainSearchTerm('');
                          }}
                          style={{
                            padding: '10px',
                            cursor: isSelected ? 'not-allowed' : 'pointer',
                            backgroundColor: isSelected ? '#2a2a2a' : 'transparent',
                            color: isSelected ? '#888' : 'white',
                            borderBottom: '1px solid #333',
                            opacity: isSelected ? 0.6 : 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: '8px',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = '#374151';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.target.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          <span>
                          {acc.code ? `${acc.code} - ` : ''}
                            {isRTL ? (acc.name_ar  || acc.name_en  || acc.name ) : (acc.name_en   || acc.name_ar  || acc.name )}
                          </span>
                          {isSelected && (
                            <span style={{ fontSize: '12px', color: '#f59f00' }}>
                              {t('receiptVouchers.add.accountAlreadySelected', { defaultValue: 'This account is already selected' })}
                            </span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ padding: '10px', color: '#888', textAlign: 'center', direction: dir }}>{t('common.noResults', { defaultValue: 'No results found' })}</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Dropdown */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <label style={{ display: 'block', fontSize: 14, textAlign: textAlign, flex: 1, direction: dir }}>
              {t('notesReceivable333.form.project', { defaultValue: 'المشروع' })}
            </label>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div
              data-dropdown-container
              style={{ flex: 1, position: 'relative' }}
            >
              <div
                data-dropdown-toggle
                onClick={(e) => {
                  e.stopPropagation();
                  setProjectDropdownOpen((p) => !p);
                }}
                style={{
                  width: '100%',
                  padding: 12,
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  boxSizing: 'border-box',
                  direction: dir,
                  textAlign: textAlign
                }}
              >
                <span>{renderProjectDisplay(form.project_id)}</span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              {projectDropdownOpen && (
                <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
                  <div style={{ padding: 8 }}>
                    <input
                      ref={projectSearchInputRef}
                      type="text"
                      placeholder={t('quotations.add.search', { defaultValue: 'ابحث عن مشروع' })}
                      value={projectSearchTerm}
                      onChange={(e) => setProjectSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: 8,
                        backgroundColor: '#14263b',
                        border: '1px solid #444',
                        borderRadius: 6,
                        color: 'white',
                        fontSize: 14,
                        direction: dir,
                        textAlign: textAlign
                      }}
                      dir={dir}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {projectsPagination && projectsPagination.last_page >= 1 && (
                    <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <span style={{ color: '#ccc', fontSize: 11, direction: dir, textAlign: textAlign }}>{t('notesPayable.form.page', { defaultValue: 'Page' })} {projectsPagination.current_page} {t('notesPayable.form.of', { defaultValue: 'of' })} {projectsPagination.last_page}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (projectsPagination.current_page > 1) {
                              setProjectCurrentPage(1);
                            }
                          }}
                          disabled={projectsPagination.current_page === 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: projectsPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                            color: projectsPagination.current_page === 1 ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: projectsPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                        >
                          ««
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (projectsPagination.current_page > 1) {
                              setProjectCurrentPage(projectsPagination.current_page - 1);
                            }
                          }}
                          disabled={projectsPagination.current_page === 1}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: projectsPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                            color: projectsPagination.current_page === 1 ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: projectsPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                        >
                          ‹
                        </button>
                        {(() => {
                          const pages = [];
                          const current = projectsPagination.current_page;
                          const last = projectsPagination.last_page;
                          const showPages = 5;
                          let start = Math.max(1, current - Math.floor(showPages / 2));
                          let end = Math.min(last, start + showPages - 1);
                          if (end - start < showPages - 1) {
                            start = Math.max(1, end - showPages + 1);
                          }
                          for (let i = start; i <= end; i++) {
                            pages.push(i);
                          }
                          return pages.map((page) => (
                            <button
                              key={page}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setProjectCurrentPage(page);
                              }}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                color: 'white',
                                border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: 10,
                                fontWeight: page === current ? 'bold' : 'normal',
                                minWidth: '28px',
                              }}
                            >
                              {page}
                            </button>
                          ));
                        })()}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (projectsPagination.current_page < projectsPagination.last_page) {
                              setProjectCurrentPage(projectsPagination.current_page + 1);
                            }
                          }}
                          disabled={projectsPagination.current_page === projectsPagination.last_page}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: projectsPagination.current_page === projectsPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                            color: projectsPagination.current_page === projectsPagination.last_page ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: projectsPagination.current_page === projectsPagination.last_page ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (projectsPagination.current_page < projectsPagination.last_page) {
                              setProjectCurrentPage(projectsPagination.last_page);
                            }
                          }}
                          disabled={projectsPagination.current_page === projectsPagination.last_page}
                          style={{
                            padding: '4px 8px',
                            backgroundColor: projectsPagination.current_page === projectsPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                            color: projectsPagination.current_page === projectsPagination.last_page ? '#666' : 'white',
                            border: '1px solid #444',
                            borderRadius: '4px',
                            cursor: projectsPagination.current_page === projectsPagination.last_page ? 'not-allowed' : 'pointer',
                            fontSize: 10,
                            fontWeight: 'bold',
                            minWidth: '28px',
                          }}
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  )}
                  <div>
                    {(projects || []).map((p) => (
                      <div
                        key={p.id}
                        onClick={() => {
                          setForm((prev) => ({ ...prev, project_id: p.id }));
                          setProjectDropdownOpen(false);
                          setProjectSearchTerm('');
                        }}
                        style={{
                          padding: 10,
                          cursor: 'pointer',
                          borderTop: '1px solid #333',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <span>{renderDropdownItemLabel(p, 'code', 'name_ar', 'name_en')}</span>
                      </div>
                    ))}
                    {(projects || []).length === 0 && (
                      <div style={{ padding: 12, textAlign: 'center', color: '#bbb' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setShowAddProjectModal(true);
              }}
              style={{
                backgroundColor: '#AC2000',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: '44px',
                height: '44px'
              }}
              title={t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}
            >
              <FaPlus />
            </button>
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', marginBottom: 6, direction: dir, textAlign: textAlign }}>{t('receiptVouchers.add.notes', { defaultValue: 'Notes' })}</label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
            rows={3}
            style={{
              width: '100%',
              backgroundColor: '#1a1f2e',
              border: '1px solid #333',
              color: 'white',
              borderRadius: 8,
              padding: '10px',
              resize: 'vertical',
              direction: dir,
              textAlign: textAlign,
            }}
          />
        </div>

        <div style={{ marginTop: '16px' }}>
          {form.details.map((line, idx) => {
            const filteredAccounts = getFilteredAccountsForLine(idx);
            return (
              <div key={line.id} style={{ marginBottom: '16px', width: '100%' }}>
                {/* Headers Row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr'
                      : isTablet
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 1.5fr) minmax(0, 1.5fr) 50px',
                    gap: '10px',
                    marginBottom: '6px',
                    alignItems: 'center',
                    width: '100%',
                  }}
                >
                  <label style={{ fontSize: '14px', color: '#9ca3af', textAlign, fontWeight: '500' }}>
                    {idx === 0 ? t('receiptVouchers.add.from', { defaultValue: 'From' }) : ''}
                  </label>
                  <div></div>
                  <div></div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'space-between' }}>
                    <label style={{ fontSize: '14px', color: '#9ca3af', textAlign, fontWeight: '500' }}>
                      {t('paymentVouchers.form.costCenter', { defaultValue: 'Cost Center' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCostCenterModalLineIndex(idx);
                        setShowAddCostCenterModal(true);
                      }}
                      style={{
                        backgroundColor: '#AC2000',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '24px',
                        height: '24px',
                        flexShrink: 0,
                      }}
                      title={t('paymentVouchers.form.addCostCenter', { defaultValue: 'Add Cost Center' })}
                    >
                      <FaPlus style={{ fontSize: '12px' }} />
                    </button>
                  </div>
                  <div></div>
                </div>
                {/* Inputs Row */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile
                      ? '1fr'
                      : isTablet
                        ? 'repeat(2, minmax(0, 1fr))'
                        : 'minmax(0, 1.3fr) minmax(0, 1.1fr) minmax(0, 1.5fr) minmax(0, 1.5fr) 50px',
                    gap: '10px',
                    alignItems: 'start',
                    width: '100%',
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <div
                      onClick={() => {
                        setDetailDropdowns((prev) => ({
                          ...prev,
                          [idx]: !prev[idx],
                        }));
                        setTimeout(() => {
                          if (detailSearchInputRefs.current[idx]) {
                            detailSearchInputRefs.current[idx].focus();
                          }
                        }, 100);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                      }}
                    >
                      <span>
                        {selectedDetailAccounts[line.id] && (String(selectedDetailAccounts[line.id].id) === String(line.account_id) || String(selectedDetailAccounts[line.id].account_id) === String(line.account_id))
                          ? (isRTL ? (selectedDetailAccounts[line.id].name_ar || selectedDetailAccounts[line.id].name_en || selectedDetailAccounts[line.id].name) : (selectedDetailAccounts[line.id].name_en || selectedDetailAccounts[line.id].name_ar || selectedDetailAccounts[line.id].name))
                          : renderAccountDisplay(line.account_id)}
                      </span>
                      <span style={{ fontSize: '12px' }}>▼</span>
                    </div>
                    {detailDropdowns[idx] && (
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'transparent' }}
                        aria-hidden="true"
                        onClick={() => setDetailDropdowns((prev) => ({ ...prev, [idx]: false }))}
                      />
                    )}
                    {detailDropdowns[idx] && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: '0 0 8px 8px',
                          zIndex: 1000,
                          maxHeight: '400px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          ref={(el) => {
                            detailSearchInputRefs.current[idx] = el;
                          }}
                          type="text"
                          placeholder={t('receiptVouchers.add.searchAccount', { defaultValue: 'Search for account...' })}
                          value={detailSearchTerms[idx] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDetailSearchTerms((prev) => ({ ...prev, [idx]: value }));
                            if (value) {
                              dispatch(getPostingAccounts(value));
                            } else {
                              dispatch(getPostingAccounts());
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#2d3748',
                            border: 'none',
                            borderBottom: '1px solid #333',
                            color: 'white',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            direction: dir,
                          }}
                          dir={dir}
                        />
                        <div>
                          {filteredAccounts.length > 0 ? (
                            filteredAccounts.map((acc) => {
                              const accId = acc.id ?? acc.account_id;
                              const isSelectedInOtherLine = form.details.some((detailItem, detailIdx) => {
                                if (detailIdx === idx) return false;
                                const otherId = detailItem.account_id ? String(detailItem.account_id) : '';
                                return otherId !== '' && otherId === String(accId);
                              });
                              const isSelected = String(line.account_id) === String(accId);
                              const isDisabled = isSelectedInOtherLine;
                              const accountName = isRTL ? (acc.name_ar || acc.name || '') : (acc.name_en || acc.name || acc.name_ar || '');
                              return (
                                <div
                                  key={acc.id ?? acc.account_id ?? accId}
                                  role="option"
                                  aria-selected={isSelected}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (isDisabled) return;
                                    updateLine(idx, 'account_id', accId);
                                    setSelectedDetailAccounts((prev) => ({ ...prev, [line.id]: acc }));
                                    setDetailDropdowns((prev) => ({ ...prev, [idx]: false }));
                                    setDetailSearchTerms((prev) => ({ ...prev, [idx]: '' }));
                                  }}
                                  style={{
                                    padding: '10px',
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: isSelected
                                      ? '#AC2000'
                                      : isDisabled
                                        ? '#2a2a2a'
                                        : 'transparent',
                                    color: isDisabled ? '#888' : 'white',
                                    borderBottom: '1px solid #333',
                                    opacity: isDisabled ? 0.6 : 1,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isDisabled && !isSelected) {
                                      e.target.style.backgroundColor = '#374151';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isDisabled && !isSelected) {
                                      e.target.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <span>
                                    {acc.code ? `${acc.code} - ` : ''}
                                    {accountName}
                                  </span>
                                  {isDisabled && (
                                    <span style={{ fontSize: '12px', color: '#f59f00' }}>
                                      {t('receiptVouchers.add.accountAlreadySelected', { defaultValue: 'Already selected' })}
                                    </span>
                                  )}
                                </div>
                              );
                            })
                          ) : (
                            <div style={{ padding: '10px', color: '#888', textAlign: 'center', direction: dir }}>{t('common.noResults', { defaultValue: 'No results found' })}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="number"
                    min="0"
                    step="any"
                    placeholder={t('receiptVouchers.add.amount', { defaultValue: 'Amount' })}
                    value={line.amount}
                    onChange={(e) => updateLine(idx, 'amount', e.target.value)}
                    style={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      color: 'white',
                      borderRadius: 8,
                      padding: '6px 10px',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      fontSize: '14px',
                      height: '36px',
                    }}
                  />
                  <textarea
                    placeholder={t('receiptVouchers.add.description', { defaultValue: 'Description' })}
                    value={line.description}
                    onChange={(e) => updateLine(idx, 'description', e.target.value)}
                    style={{
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      color: 'white',
                      borderRadius: 8,
                      padding: '6px 10px',
                      width: '100%',
                      boxSizing: 'border-box',
                      minWidth: 0,
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      height: '36px',
                      minHeight: '36px',
                      lineHeight: '1.5',
                    }}
                  />
                  <div style={{ position: 'relative' }}>
                    <div
                      onClick={() => {
                        setCostCenterDropdowns((prev) => ({
                          ...prev,
                          [idx]: !prev[idx],
                        }));
                        if (!costCenterCurrentPages[idx]) {
                          setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: 1 }));
                        }
                        // Set source to line
                        setCostCenterSource('line');
                        setCostCenterModalLineIndex(idx);

                        setTimeout(() => {
                          if (costCenterSearchInputRefs.current[idx]) {
                            costCenterSearchInputRefs.current[idx].focus();
                          }
                        }, 100);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px 10px',
                        backgroundColor: '#1a1f2e',
                        border: '1px solid #333',
                        borderRadius: '8px',
                        color: 'white',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxSizing: 'border-box',
                        fontSize: '14px',
                        height: '36px',
                      }}
                    >
                      <span>
                        {selectedDetailCostCenters[line.id] && (String(selectedDetailCostCenters[line.id].id) === String(line.cost_center_id))
                          ? `${selectedDetailCostCenters[line.id].code ? `${selectedDetailCostCenters[line.id].code} - ` : ''}${isRTL ? (selectedDetailCostCenters[line.id].name_ar || selectedDetailCostCenters[line.id].name || '') : (selectedDetailCostCenters[line.id].name_en || selectedDetailCostCenters[line.id].name || selectedDetailCostCenters[line.id].name_ar || '')}`
                          : renderCostCenterDisplay(line.cost_center_id)}
                      </span>
                      <span style={{ fontSize: '12px' }}>▼</span>
                    </div>
                    {costCenterDropdowns[idx] && (
                      <div
                        style={{ position: 'fixed', inset: 0, zIndex: 998, background: 'transparent' }}
                        aria-hidden="true"
                        onClick={() => setCostCenterDropdowns((prev) => ({ ...prev, [idx]: false }))}
                      />
                    )}
                    {costCenterDropdowns[idx] && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: '#1a1f2e',
                          border: '1px solid #333',
                          borderRadius: '0 0 8px 8px',
                          zIndex: 1000,
                          maxHeight: '400px',
                          overflowY: 'auto',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.3)',
                          marginTop: 4,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          ref={(el) => {
                            costCenterSearchInputRefs.current[idx] = el;
                          }}
                          type="text"
                          placeholder={t('paymentVouchers.form.searchCostCenter', { defaultValue: 'Search Cost Center' })}
                          value={costCenterSearchTerms[idx] || ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            setCostCenterSearchTerms((prev) => ({ ...prev, [idx]: value }));
                          }}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '10px',
                            backgroundColor: '#2d3748',
                            border: 'none',
                            borderBottom: '1px solid #333',
                            color: 'white',
                            fontSize: '14px',
                            boxSizing: 'border-box',
                            direction: dir,
                          }}
                          dir={dir}
                        />
                        {costCentersPagination && costCentersPagination.last_page > 1 && (
                          <div style={{ padding: '8px', borderTop: '1px solid #333', borderBottom: '1px solid #333', background: '#14263b' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                              <span style={{ color: '#ccc', fontSize: 11 }}>
                                الصفحة {costCentersPagination.current_page} من {costCentersPagination.last_page}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page > 1) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: 1 }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === 1}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === 1 ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                ««
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page > 1) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: costCentersPagination.current_page - 1 }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === 1}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === 1 ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === 1 ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                ‹
                              </button>
                              {(() => {
                                const pages = [];
                                const current = costCentersPagination.current_page;
                                const last = costCentersPagination.last_page;
                                const showPages = 5;
                                let start = Math.max(1, current - Math.floor(showPages / 2));
                                let end = Math.min(last, start + showPages - 1);
                                if (end - start < showPages - 1) {
                                  start = Math.max(1, end - showPages + 1);
                                }
                                for (let i = start; i <= end; i++) {
                                  pages.push(i);
                                }
                                return pages.map((page) => (
                                  <button
                                    key={page}
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: page }));
                                    }}
                                    style={{
                                      padding: '4px 8px',
                                      backgroundColor: page === current ? '#AC2000' : '#1a1f2e',
                                      color: 'white',
                                      border: page === current ? '1px solid #AC2000' : '1px solid #444',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontSize: 10,
                                      fontWeight: page === current ? 'bold' : 'normal',
                                      minWidth: '28px',
                                    }}
                                  >
                                    {page}
                                  </button>
                                ));
                              })()}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page < costCentersPagination.last_page) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: costCentersPagination.current_page + 1 }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === costCentersPagination.last_page}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === costCentersPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === costCentersPagination.last_page ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === costCentersPagination.last_page ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                ›
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (costCentersPagination.current_page < costCentersPagination.last_page) {
                                    setCostCenterCurrentPages((prev) => ({ ...prev, [idx]: costCentersPagination.last_page }));
                                  }
                                }}
                                disabled={costCentersPagination.current_page === costCentersPagination.last_page}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: costCentersPagination.current_page === costCentersPagination.last_page ? '#2a2a2a' : '#1a1f2e',
                                  color: costCentersPagination.current_page === costCentersPagination.last_page ? '#666' : 'white',
                                  border: '1px solid #444',
                                  borderRadius: '4px',
                                  cursor: costCentersPagination.current_page === costCentersPagination.last_page ? 'not-allowed' : 'pointer',
                                  fontSize: 10,
                                  fontWeight: 'bold',
                                  minWidth: '28px',
                                }}
                              >
                                »»
                              </button>
                            </div>
                          </div>
                        )}
                        <div>
                          {(() => {
                            const filteredCC = getFilteredCostCentersForLine(idx);
                            return filteredCC.length > 0 ? (
                              filteredCC.map((cc) => (
                                <div
                                  key={cc.id}
                                  role="option"
                                  aria-selected={String(line.cost_center_id) === String(cc.id)}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    updateLine(idx, 'cost_center_id', cc.id);
                                    setSelectedDetailCostCenters((prev) => ({ ...prev, [line.id]: cc }));
                                    setCostCenterDropdowns((prev) => ({ ...prev, [idx]: false }));
                                    setCostCenterSearchTerms((prev) => ({ ...prev, [idx]: '' }));
                                  }}
                                  style={{
                                    padding: '10px',
                                    cursor: 'pointer',
                                    backgroundColor: String(line.cost_center_id) === String(cc.id) ? '#AC2000' : 'transparent',
                                    color: 'white',
                                    borderBottom: '1px solid #333',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                  onMouseEnter={(e) => {
                                    if (String(line.cost_center_id) !== String(cc.id)) {
                                      e.target.style.backgroundColor = '#374151';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (String(line.cost_center_id) !== String(cc.id)) {
                                      e.target.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  <span>
                                    {cc.code ? `${cc.code} - ` : ''}
                                    {isRTL ? (cc.name_ar || cc.name || '') : (cc.name_en || cc.name || cc.name_ar || '')}
                                  </span>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '10px', color: '#888', textAlign: 'center' }}>{t('paymentVouchers.form.noResults', { defaultValue: 'No Results' })}</div>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', width: '100%', padding: 0 }}>
                    <button
                      type="button"
                      onClick={() => removeDetailLine(idx)}
                      disabled={form.details.length === 1}
                      style={{
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: 8,
                        padding: '8px',
                        cursor: form.details.length === 1 ? 'not-allowed' : 'pointer',
                        opacity: form.details.length === 1 ? 0.6 : 1,
                        width: '36px',
                        height: '36px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        alignSelf: 'flex-start',
                      }}
                      title={t('common.delete', { defaultValue: 'Delete' })}
                    >
                      <FaTrash style={{ fontSize: '14px' }} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={addDetailLine}
            style={{
              background: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              direction: dir
            }}
          >
            + {t('receiptVouchers.add.addLine', { defaultValue: 'Add Line' })}
          </button>
        </div>

        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: '#1a1f2e',
            borderRadius: '8px',
            border: '1px solid #333',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', direction: dir }}>
            <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: textAlign }}>{t('receiptVouchers.add.totalAmount', { defaultValue: 'Total Amount' })}</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'white', textAlign: textAlign }}>
              {totalAmount.toFixed(2)}
            </div>
          </div>
        </div>

        <div style={{ marginTop: '24px', marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '15px',
              fontWeight: 'bold',
              color: 'white',
              fontSize: '16px',
            }}
          >
            {t('receiptVouchers.add.attachments', { defaultValue: 'Attachments (Optional)' })}
          </label>

          <div style={{ marginBottom: '20px' }}>
            <input
              type="file"
              id="receiptVoucherAttachments"
              multiple
              accept="*/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="receiptVoucherAttachments"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                backgroundColor: 'transparent',
                color: 'white',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                border: '2px solid #AC2000',
                transition: 'all 0.3s ease',
              }}
            >
              <FaPaperclip />
              {t('receiptVouchers.add.addAttachments', { defaultValue: 'Add Attachments' })}
            </label>
          </div>

          {attachments.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 160 : 200}px, 1fr))`,
                gap: '15px',
                marginTop: '20px',
              }}
            >
              {attachments.map((attachment) => (
                <div
                  key={attachment.id}
                  style={{
                    backgroundColor: '#1a1f2e',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #333',
                    position: 'relative',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => removeAttachment(attachment.id)}
                    style={{
                      position: 'absolute',
                      top: '5px',
                      left: '5px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '50%',
                      width: '25px',
                      height: '25px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                    }}
                  >
                    <FaTimes />
                  </button>
                  <a
                    href={attachment.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={isRTL ? 'اضغط لفتح المرفق' : 'Click to open attachment'}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '8px',
                      textDecoration: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <FaPaperclip style={{ fontSize: '24px', color: '#AC2000' }} />
                    <div
                      style={{
                        fontSize: '12px',
                        color: 'white',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                      }}
                    >
                      {attachment.name}
                    </div>
                    <div style={{ fontSize: '10px', color: '#999' }}>
                      {(attachment.size / 1024).toFixed(2)} KB
                    </div>
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '24px',
            display: 'flex',
            gap: '12px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/receipt-vouchers')}
            style={{
              background: '#666',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
            }}
          >
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </button>

          <button
            type="submit"
            disabled={isLoading}
            style={{
              background: '#AC2000',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 16px',
              cursor: 'pointer',
              opacity: isLoading ? 0.7 : 1,
              direction: dir
            }}
          >
            {isLoading ? t('receiptVouchers.add.saving', { defaultValue: 'Saving...' }) : t('common.save', { defaultValue: 'Save' })}
          </button>
        </div>
      </form>

      {/* Add Cost Center Modal */}
      {showAddCostCenterModal && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 999999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAddCostCenterModal(false);
              setCostCenterModalLineIndex(null);
              setCostCenterForm({
                name_ar: '',
                name_en: '',
                code: '',
              });
            }
          }}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('paymentVouchers.form.modals.addCostCenter.title', { defaultValue: 'Add Cost Center' })}</h2>
              <button
                onClick={() => {
                  setShowAddCostCenterModal(false);
                  setCostCenterForm({
                    name_ar: '',
                    name_en: '',
                    code: '',
                  });
                  setCostCenterModalLineIndex(null);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCostCenter}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('paymentVouchers.form.modals.addCostCenter.nameAr', { defaultValue: 'Arabic Name' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={costCenterForm.name_ar}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('paymentVouchers.form.modals.addCostCenter.nameEn', { defaultValue: 'English Name' })}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={costCenterForm.name_en}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('paymentVouchers.form.modals.addCostCenter.code', { defaultValue: 'Code' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={costCenterForm.code}
                    onChange={handleCostCenterFormChange}
                    onFocus={(e) => e.stopPropagation()}
                    onBlur={(e) => e.stopPropagation()}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddCostCenterModal(false);
                    setCostCenterForm({
                      name_ar: '',
                      name_en: '',
                      code: '',
                    });
                    setCostCenterModalLineIndex(null);
                  }}
                  style={{
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  {t('paymentVouchers.form.modals.addCostCenter.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCostCenter}
                  style={{
                    backgroundColor: isSubmittingCostCenter ? '#666' : '#AC2000',
                    color: 'white',
                    border: 'none',
                    padding: '12px 25px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    cursor: isSubmittingCostCenter ? 'not-allowed' : 'pointer',
                    opacity: isSubmittingCostCenter ? 0.7 : 1
                  }}
                >
                  {isSubmittingCostCenter ? t('paymentVouchers.form.modals.addCostCenter.saving', { defaultValue: 'Saving...' }) : t('paymentVouchers.form.modals.addCostCenter.save', { defaultValue: 'Save' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Preview Modal */}
      {/* Add Project Modal */}
      {showAddProjectModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddProjectModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
              <button
                onClick={() => setShowAddProjectModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={projectForm.name_ar}
                    onChange={handleProjectFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={projectForm.name_en}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addProject.code', { defaultValue: 'كود المشروع' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={projectForm.code}
                    onChange={handleProjectFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCustomerModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setTaxDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      setProjModalCustomerDropdownOpen((p) => !p);
                    }}
                    style={{ flex: 1, padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                      {projectForm.customer_id ? (customers || []).find(c => String(c.id) === String(projectForm.customer_id)) ? renderDropdownItemLabel((customers || []).find(c => String(c.id) === String(projectForm.customer_id)), 'code', 'name_ar', 'name_en') : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' }) : t('quotations.add.modals.addProject.selectCustomer', { defaultValue: 'اختر العميل' })}
                    </span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {projModalCustomerDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={projModalCustomerDropdownOpen}
                      onToggle={setProjModalCustomerDropdownOpen}
                      searchTerm={projModalCustomerSearchTerm}
                      onSearchChange={(val) => {
                        setProjModalCustomerSearchTerm(val);
                        if (projModalCustomerSearchTimeoutRef.current) clearTimeout(projModalCustomerSearchTimeoutRef.current);
                        projModalCustomerSearchTimeoutRef.current = setTimeout(() => {
                          setProjModalCustomerCurrentPage(1);
                          dispatch(getCustomers({ page: 1, per_page: 10, search: val || undefined }));
                        }, 300);
                      }}
                      searchInputRef={projModalCustomerSearchInputRef}
                      items={Array.isArray(customers) ? customers : []}
                      pagination={customersPagination}
                      currentPage={projModalCustomerCurrentPage}
                      onPageChange={(page) => {
                        setProjModalCustomerCurrentPage(page);
                        dispatch(getCustomers({ page, per_page: 10, search: projModalCustomerSearchTerm || undefined }));
                      }}
                      onSelect={(id) => {
                        setProjectForm((prev) => ({ ...prev, customer_id: id }));
                        setProjModalCustomerDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.customer', { defaultValue: 'عميل' })}
                    />
                  )}
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البداية' })}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ النهاية' })}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                    <option value="planned">{t('quotations.add.modals.addProject.statusOptions.planned', { defaultValue: 'مخطط' })}</option>
                    <option value="in_progress">{t('quotations.add.modals.addProject.statusOptions.inProgress', { defaultValue: 'قيد التنفيذ' })}</option>
                    <option value="paused">{t('quotations.add.modals.addProject.statusOptions.paused', { defaultValue: 'متوقف' })}</option>
                    <option value="completed">{t('quotations.add.modals.addProject.statusOptions.completed', { defaultValue: 'مكتمل' })}</option>
                    <option value="cancelled">{t('quotations.add.modals.addProject.statusOptions.cancelled', { defaultValue: 'ملغى' })}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: 'white', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                  <input type="number"  name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: 12, backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }} />
                </div>

                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddTaxModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setCostCenterDropdownOpen(false);
                      if (taxDropdownOpen) { setTaxSearchTerm(''); setTaxCurrentPage(1); }
                      setTaxDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.tax_id ? renderDropdownItemLabel((taxes || []).find(t => t.id === projectForm.tax_id) || {}, '', 'name_ar', 'name_en') : t('quotations.add.selectTax', { defaultValue: 'اختر الضريبة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {taxDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={taxDropdownOpen}
                      onToggle={setTaxDropdownOpen}
                      searchTerm={taxSearchTerm}
                      onSearchChange={setTaxSearchTerm}
                      searchInputRef={taxSearchInputRef}
                      items={taxes || []}
                      pagination={taxesPagination}
                      currentPage={taxCurrentPage}
                      onPageChange={setTaxCurrentPage}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, tax_id: id }));
                        setTaxDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}
                    />
                  )}
                </div>
                <div style={{ position: 'relative' }} data-dropdown-container>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <label style={{ display: 'block', fontSize: 13, fontWeight: 'bold', color: 'white', flex: 1, textAlign: isRTL ? 'right' : 'left' }}>
                      {t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    </label>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setShowAddCostCenterModal(true); }}
                      style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}
                    >
                      <FaPlus /> {t('common.add', { defaultValue: 'إضافة' })}
                    </button>
                  </div>
                  <div
                    data-dropdown-toggle
                    onClick={() => {
                      setProjModalCustomerDropdownOpen(false);
                      setTaxDropdownOpen(false);
                      if (costCenterDropdownOpen) { setCostCenterSearchTerm(''); setCostCenterCurrentPage(1); }
                      setCostCenterDropdownOpen((p) => !p);
                    }}
                    style={{ width: '100%', padding: '12px 15px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: 8, color: 'white', fontSize: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', boxSizing: 'border-box' }}
                  >
                    <span>{projectForm.cost_center_id ? renderDropdownItemLabel((costCenters || []).find(c => c.id === projectForm.cost_center_id) || {}, 'code', 'name_ar', 'name_en') : t('quotations.add.selectCostCenter', { defaultValue: 'اختر مركز التكلفة' })}</span>
                    <span style={{ fontSize: 12 }}>▼</span>
                  </div>
                  {costCenterDropdownOpen && (
                    <DropdownWithSearch
                      isOpen={costCenterDropdownOpen}
                      onToggle={setCostCenterDropdownOpen}
                      searchTerm={costCenterSearchTerm}
                      onSearchChange={(val) => {
                        setCostCenterSearchTerm(val);
                        if (costCenterSearchTimeoutRef.current) clearTimeout(costCenterSearchTimeoutRef.current);
                        costCenterSearchTimeoutRef.current = setTimeout(() => {
                          setCostCenterCurrentPage(1);
                          dispatch(getCostCenters({ page: 1, per_page: 10, search: val || undefined }));
                        }, 300);
                      }}
                      searchInputRef={costCenterSearchInputRef}
                      items={costCenters || []}
                      pagination={costCentersPagination}
                      currentPage={costCenterCurrentPage}
                      onPageChange={(page) => {
                        setCostCenterCurrentPage(page);
                        dispatch(getCostCenters({ page, per_page: 10, search: costCenterSearchTerm || undefined }));
                      }}
                      onSelect={(id) => {
                        setProjectForm(prev => ({ ...prev, cost_center_id: id }));
                        setCostCenterDropdownOpen(false);
                      }}
                      label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}
                    />
                  )}
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  ref={projectNotesTextareaRef}
                  name="notes"
                  value={projectForm.notes}
                  onChange={handleProjectFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddProjectModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingProject}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingProject ? 'not-allowed' : 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}
                >
                  {isSubmittingProject ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      {/* Add Tax Modal */}
      {showAddTaxModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddTaxModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}
              </h2>
              <button
                onClick={() => setShowAddTaxModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'grid', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={taxForm.name_ar}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={taxForm.name_en}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة (%)' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="number"
                    name="rate"
                    value={taxForm.rate}
                    onChange={handleTaxFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={taxForm.is_active}
                    onChange={(e) => setTaxForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('quotations.add.modals.addTax.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddTaxModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 999999,
            padding: '20px'
          }}
          onClick={() => setShowAddCustomerModal(false)}
        >
          <div
            style={{
              backgroundColor: '#1b3350',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid #333',
              direction: isRTL ? 'rtl' : 'ltr'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>
                {t('customers.add.title', { defaultValue: 'إضافة عميل جديد' })}
              </h2>
              <button
                onClick={() => setShowAddCustomerModal(false)}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  color: 'white',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ar"
                    value={customerForm.name_ar}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'rtl',
                      textAlign: 'right'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={customerForm.name_en}
                    onChange={handleCustomerFormChange}
                    required
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.code', { defaultValue: 'كود العميل' })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={customerForm.code}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.contactPerson', { defaultValue: 'الشخص المسؤول' })}
                  </label>
                  <input
                    type="text"
                    name="contact_person"
                    value={customerForm.contact_person}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.email', { defaultValue: 'البريد الإلكتروني' })}
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={customerForm.email}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: 'ltr',
                      textAlign: 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.phone', { defaultValue: 'رقم الهاتف' })}
                  </label>
                  <input
                    type="text"
                    name="phone"
                    value={customerForm.phone}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                    {t('customers.add.taxNumber', { defaultValue: 'الرقم الضريبي' })}
                  </label>
                  <input
                    type="text"
                    name="tax_number"
                    value={customerForm.tax_number}
                    onChange={handleCustomerFormChange}
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      backgroundColor: '#1a1f2e',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      color: 'white',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      textAlign: isRTL ? 'right' : 'left'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={customerForm.is_active}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, is_active: e.target.checked }))}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label style={{ color: 'white', fontSize: '14px', cursor: 'pointer' }}>
                    {t('customers.add.status.isActive', { defaultValue: 'نشط' })}
                  </label>
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('customers.add.address', { defaultValue: 'العنوان' })}
                </label>
                <input
                  type="text"
                  name="address"
                  value={customerForm.address}
                  onChange={handleCustomerFormChange}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left' }}>
                  {t('customers.add.notes', { defaultValue: 'ملاحظات' })}
                </label>
                <textarea
                  name="notes"
                  value={customerForm.notes}
                  onChange={handleCustomerFormChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 15px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '14px',
                    resize: 'vertical',
                    direction: isRTL ? 'rtl' : 'ltr',
                    textAlign: isRTL ? 'right' : 'left'
                  }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '30px' }}>
                <button
                  type="button"
                  onClick={() => setShowAddCustomerModal(false)}
                  style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
                >
                  {t('common.cancel', { defaultValue: 'إلغاء' })}
                </button>
                <button
                  type="submit"
                  disabled={isSubmittingCustomer}
                  style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '12px 25px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: isSubmittingCustomer ? 'not-allowed' : 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}
                >
                  {isSubmittingCustomer ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      {/* Add Project Modal */}
      {showAddProjectModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999999, padding: '20px' }} onClick={() => setShowAddProjectModal(false)}>
          <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '800px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #333', direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white', textAlign: isRTL ? 'right' : 'left' }}>{t('quotations.add.modals.addProject.title', { defaultValue: 'إضافة مشروع جديد' })}</h2>
              <button onClick={() => setShowAddProjectModal(false)} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><FaTimes /></button>
            </div>
            <form onSubmit={handleAddProject}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.code', { defaultValue: 'الكود' })}</label>
                  <input type="text" name="code" value={projectForm.code} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.nameAr', { defaultValue: 'الاسم بالعربية' })} <span style={{ color: 'red' }}>*</span></label>
                  <input type="text" name="name_ar" value={projectForm.name_ar} onChange={handleProjectFormChange} required style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.nameEn', { defaultValue: 'الاسم بالإنجليزية' })}</label>
                  <input type="text" name="name_en" value={projectForm.name_en} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ display: 'block', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })}</label>
                    <button type="button" onClick={() => setShowAddCustomerModal(true)} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}>{t('common.add', { defaultValue: 'إضافة' })}</button>
                  </div>
                  <div onClick={() => setProjModalCustomerDropdownOpen(!projModalCustomerDropdownOpen)} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{projectForm.customer_id ? renderDropdownItemLabel(customers.find(c => c.id === projectForm.customer_id) || {}, 'code', 'name_ar', 'name_en') : t('common999.select', { defaultValue: 'اختر' })}</span>
                    <span>▼</span>
                  </div>
                  {projModalCustomerDropdownOpen && (
                    <DropdownWithSearch isOpen={projModalCustomerDropdownOpen} onToggle={setProjModalCustomerDropdownOpen} searchTerm={projModalCustomerSearchTerm} onSearchChange={setProjModalCustomerSearchTerm} searchInputRef={projModalCustomerSearchInputRef} items={customers || []} pagination={customersPagination} currentPage={projModalCustomerCurrentPage} onPageChange={setProjModalCustomerCurrentPage} onSelect={(id) => { setProjectForm(prev => ({ ...prev, customer_id: id })); setProjModalCustomerDropdownOpen(false); }} label={t('quotations.add.modals.addProject.customer', { defaultValue: 'العميل' })} />
                  )}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.startDate', { defaultValue: 'تاريخ البدء' })}</label>
                  <input type="date" name="start_date" value={projectForm.start_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.endDate', { defaultValue: 'تاريخ الانتهاء' })}</label>
                  <input type="date" name="end_date" value={projectForm.end_date} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.status', { defaultValue: 'الحالة' })}</label>
                  <select name="status" value={projectForm.status} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }}>
                    <option value="planned">{t('project.status.planned', { defaultValue: 'مخطط' })}</option>
                    <option value="in_progress">{t('project.status.in_progress', { defaultValue: 'قيد التنفيذ' })}</option>
                    <option value="completed">{t('project.status.completed', { defaultValue: 'مكتمل' })}</option>
                    <option value="on_hold">{t('project.status.on_hold', { defaultValue: 'معلق' })}</option>
                    <option value="cancelled">{t('project.status.cancelled', { defaultValue: 'ملغى' })}</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.contractValue', { defaultValue: 'قيمة العقد' })}</label>
                  <input type="number" name="contract_value" value={projectForm.contract_value} onChange={handleProjectFormChange} style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ display: 'block', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })}</label>
                    <button type="button" onClick={() => setShowAddTaxModal(true)} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}>{t('common.add', { defaultValue: 'إضافة' })}</button>
                  </div>
                  <div onClick={() => setTaxDropdownOpen(!taxDropdownOpen)} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{projectForm.tax_id ? (taxes.find(t => t.id === projectForm.tax_id)?.name_ar || t('common999.select', { defaultValue: 'اختر' })) : t('common999.select', { defaultValue: 'اختر' })}</span>
                    <span>▼</span>
                  </div>
                  {taxDropdownOpen && (
                    <DropdownWithSearch isOpen={taxDropdownOpen} onToggle={setTaxDropdownOpen} searchTerm={taxSearchTerm} onSearchChange={setTaxSearchTerm} searchInputRef={taxSearchInputRef} items={taxes || []} pagination={taxesPagination} currentPage={taxCurrentPage} onPageChange={setTaxCurrentPage} onSelect={(id) => { setProjectForm(prev => ({ ...prev, tax_id: id })); setTaxDropdownOpen(false); }} label={t('quotations.add.modals.addProject.tax', { defaultValue: 'الضريبة' })} />
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <label style={{ display: 'block', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })}</label>
                    <button type="button" onClick={() => { setShowAddCostCenterModal(true); setCostCenterSource('project'); }} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 12, cursor: 'pointer' }}>{t('common.add', { defaultValue: 'إضافة' })}</button>
                  </div>
                  <div onClick={() => setCostCenterDropdownOpen(!costCenterDropdownOpen)} style={{ padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{projectForm.cost_center_id ? renderDropdownItemLabel(costCenters.find(c => c.id === projectForm.cost_center_id) || {}, 'code', 'name_ar', 'name_en') : t('common999.select', { defaultValue: 'اختر' })}</span>
                    <span>▼</span>
                  </div>
                  {costCenterDropdownOpen && (
                    <DropdownWithSearch isOpen={costCenterDropdownOpen} onToggle={setCostCenterDropdownOpen} searchTerm={costCenterSearchTerm} onSearchChange={setCostCenterSearchTerm} searchInputRef={costCenterSearchInputRef} items={costCenters || []} pagination={costCentersPagination} currentPage={costCenterCurrentPage} onPageChange={setCostCenterCurrentPage} onSelect={(id) => { setProjectForm(prev => ({ ...prev, cost_center_id: id })); setCostCenterDropdownOpen(false); }} label={t('quotations.add.modals.addProject.costCenter', { defaultValue: 'مركز التكلفة' })} />
                  )}
                </div>
              </div>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addProject.notes', { defaultValue: 'ملاحظات' })}</label>
                <textarea name="notes" value={projectForm.notes} onChange={handleProjectFormChange} rows="3" style={{ width: '100%', padding: '12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '30px' }}>
                <button type="button" onClick={() => setShowAddProjectModal(false)} style={{ padding: '10px 20px', backgroundColor: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{t('common.cancel', { defaultValue: 'إلغاء' })}</button>
                <button type="submit" disabled={isSubmittingProject} style={{ padding: '10px 20px', backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: isSubmittingProject ? 0.7 : 1 }}>{isSubmittingProject ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}</button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      {/* Add Customer Modal */}
      {showAddCustomerModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000000, padding: '20px' }} onClick={() => setShowAddCustomerModal(false)}>
          <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #333', direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>{t('customers.add.title', { defaultValue: 'إضافة عميل جديد' })}</h2>
              <button onClick={() => setShowAddCustomerModal(false)} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}><FaTimes /></button>
            </div>
            <form onSubmit={handleAddCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.code', { defaultValue: 'الكود' })}</label>
                  <input type="text" name="code" value={customerForm.code} onChange={handleCustomerFormChange} style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.nameAr', { defaultValue: 'الاسم بالعربية' })} *</label>
                  <input type="text" name="name_ar" value={customerForm.name_ar} onChange={handleCustomerFormChange} required style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} *</label>
                  <input type="text" name="name_en" value={customerForm.name_en} onChange={handleCustomerFormChange} required style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.contactPerson', { defaultValue: 'المسئول' })}</label>
                  <input type="text" name="contact_person" value={customerForm.contact_person} onChange={handleCustomerFormChange} style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.phone', { defaultValue: 'رقم الهاتف' })}</label>
                  <input type="text" name="phone" value={customerForm.phone} onChange={handleCustomerFormChange} style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.email', { defaultValue: 'البريد الإلكتروني' })}</label>
                  <input type="email" name="email" value={customerForm.email} onChange={handleCustomerFormChange} style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.address', { defaultValue: 'العنوان' })}</label>
                  <input type="text" name="address" value={customerForm.address} onChange={handleCustomerFormChange} style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.taxNumber', { defaultValue: 'الرقم الضريبي' })}</label>
                  <input type="text" name="tax_number" value={customerForm.tax_number} onChange={handleCustomerFormChange} style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.list.status', { defaultValue: 'الحالة' })}</label>
                  <select
                    name="is_active"
                    value={customerForm.is_active}
                    onChange={(e) => setCustomerForm(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                    style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }}
                  >
                    <option value="true">{t('status.active', { defaultValue: 'نشط' })}</option>
                    <option value="false">{t('status.inactive', { defaultValue: 'غير نشط' })}</option>
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('customers.add.notes', { defaultValue: 'ملاحظات' })}</label>
                <textarea name="notes" value={customerForm.notes} onChange={handleCustomerFormChange} rows="3" style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '30px' }}>
                <button type="button" onClick={() => setShowAddCustomerModal(false)} style={{ padding: '10px 20px', backgroundColor: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{t('common.cancel', { defaultValue: 'إلغاء' })}</button>
                <button type="submit" disabled={isSubmittingCustomer} style={{ padding: '10px 20px', backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', opacity: isSubmittingCustomer ? 0.7 : 1 }}>{isSubmittingCustomer ? t('common.saving', { defaultValue: 'جاري الحفظ...' }) : t('common.save', { defaultValue: 'حفظ' })}</button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      {/* Add Tax Modal */}
      {showAddTaxModal && createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000000, padding: '20px' }} onClick={() => setShowAddTaxModal(false)}>
          <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '500px', width: '100%', border: '1px solid #333', direction: isRTL ? 'rtl' : 'ltr' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>{t('quotations.add.modals.addTax.title', { defaultValue: 'إضافة ضريبة جديدة' })}</h2>
              <button onClick={() => setShowAddTaxModal(false)} style={{ backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}><FaTimes /></button>
            </div>
            <form onSubmit={handleAddTax}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addTax.nameAr', { defaultValue: 'الاسم بالعربية' })} *</label>
                  <input type="text" name="name_ar" value={taxForm.name_ar} onChange={handleTaxFormChange} required style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addTax.nameEn', { defaultValue: 'الاسم بالإنجليزية' })} *</label>
                  <input type="text" name="name_en" value={taxForm.name_en} onChange={handleTaxFormChange} required style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', color: 'white', fontSize: '14px' }}>{t('quotations.add.modals.addTax.rate', { defaultValue: 'النسبة (%)' })} *</label>
                  <input type="number" name="rate" value={taxForm.rate} onChange={handleTaxFormChange} required  style={{ width: '100%', padding: '10px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '30px' }}>
                <button type="button" onClick={() => setShowAddTaxModal(false)} style={{ padding: '10px 20px', backgroundColor: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{t('common.cancel', { defaultValue: 'إلغاء' })}</button>
                <button type="submit" style={{ padding: '10px 20px', backgroundColor: '#AC2000', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>{t('common.save', { defaultValue: 'حفظ' })}</button>
              </div>
            </form>
          </div>
        </div>
        , document.body)}

      <PdfPreviewModal
        isOpen={isPreviewOpen}
        title={t('receiptVouchers.preview.single', { defaultValue: 'Receipt Voucher Preview' })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintFromPreview}
        isGeneratingPdf={isGeneratingPdf}
      >
        {isPreviewOpen && (
          <ReceiptVoucherSingleTemplate
            voucher={createPreviewVoucher()}
            isRTL={isRTL}
            formatDate={formatDate}
            formatCurrency={formatCurrency}
            statusLabel={statusLabel}
            publicUrl={publicUrl}
            headerLogo={headerLogo}
            isPreview={true}
            t={t}
          />
        )}
      </PdfPreviewModal>

      {/* Hidden print template */}
      {!isPreviewOpen && (
        <ReceiptVoucherSingleTemplate
          voucher={voucherForPrint || createPreviewVoucher()}
          isRTL={isRTL}
          formatDate={formatDate}
          formatCurrency={formatCurrency}
          statusLabel={statusLabel}
          publicUrl={publicUrl}
          headerLogo={headerLogo}
          isPreview={false}
          t={t}
        />
      )}
    </div>
  );
};

// Receipt Voucher Single Template Component (copied from ReceiptVouchers.jsx)
// SWAP: 'من' becomes 'إلى' and 'إلى' becomes 'من' in the template
const ReceiptVoucherSingleTemplate = ({ voucher, isRTL, formatDate, formatCurrency, statusLabel, publicUrl, headerLogo, isPreview = false, t }) => {
  if (!voucher) return null;

  const details = Array.isArray(voucher.details) ? voucher.details : [];
  const totalAmount = Number(voucher.total_amount || details.reduce((sum, line) => sum + (parseFloat(line.amount) || 0), 0));
  const accountName = voucher.account ? (isRTL ? (voucher.account.name_ar || voucher.account.name) : (voucher.account.name_en || voucher.account.name_ar || voucher.account.name)) : '';
  const accountCode = voucher.account?.code || '';

  return ( 
    <div
      id={isPreview ? `preview-receipt-voucher-pdf-${voucher.id}` : `receipt-voucher-pdf-${voucher.id}`}
      aria-hidden={!isPreview}
      style={{
        position: isPreview ? 'relative' : 'absolute',
        left: isPreview ? 'auto' : '-9999px',
        top: isPreview ? 'auto' : 0,
        width: '794px',
        minHeight: '1123px',
        backgroundColor: '#ffffff',
        color: '#000000',
        fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
        fontWeight: 'bold',
        padding: '40px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{
        border: '5px solid #000',
        padding: '3px',
        backgroundColor: '#ffffff',
        boxSizing: 'border-box',
        width: '100%',
        minHeight: '1043px',
      }}>
        <div style={{
          border: '2px solid #000',
          minHeight: '1033px',
          height: '1033px',
          padding: '18px 18px 10px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          direction: isRTL ? 'rtl' : 'ltr',
          backgroundColor: '#ffffff',
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${headerLogo})`,
            backgroundSize: '95%',
            backgroundPosition: 'center center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.08,
            zIndex: 0,
            pointerEvents: 'none'
          }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <img
              src={`${publicUrl}/HeaderAce.png`}
              alt="Header"
              style={{
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
                marginBottom: '8px',
                borderBottom: '4px double #000',
                paddingBottom: '8px',
                flexShrink: 0
              }}
              crossOrigin="anonymous"
            /> 
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
              <div style={{ textAlign: 'center', marginBottom: '20px', marginTop: '12px' }}>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#000080', margin: 0 }}>
                  {t('receiptVouchers.title', { defaultValue: 'Receipt Voucher' })}
                </h2>
              </div>

              <div style={{
                marginTop: '12px',
                border: '2px solid #000',
                borderRadius: '4px',
                overflow: 'hidden',
                fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: 'bold',
                backgroundColor: 'rgba(255, 255, 255, 0.3)'
              }}>
                <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                  <div style={{
                    width: '30%',
                    borderLeft: '1px solid #000',
                    backgroundColor: 'rgba(227, 242, 253, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    fontWeight: '700',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    color: '#1976d2',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    padding: '0 10px'
                  }}>
                    {t('receiptVouchers.add.reference', { defaultValue: 'Reference' })}:
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#000',
                    direction: isRTL ? 'rtl' : 'ltr',
                    padding: '0 10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                  }}>
                    {voucher.reference || '—'}
                  </div>
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                  <div style={{
                    width: '30%',
                    borderLeft: '1px solid #000',
                    backgroundColor: 'rgba(227, 242, 253, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    fontWeight: '700',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    color: '#1976d2',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    padding: '0 10px'
                  }}>
                    {t('receiptVouchers.add.date', { defaultValue: 'Date' })}:
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#000',
                    direction: isRTL ? 'rtl' : 'ltr',
                    padding: '0 10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                  }}>
                    {formatDate(voucher.voucher_date) || '—'}
                  </div>
                </div>

                <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                  <div style={{
                    width: '30%',
                    borderLeft: '1px solid #000',
                    backgroundColor: 'rgba(227, 242, 253, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    fontWeight: '700',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    color: '#1976d2',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    padding: '0 10px'
                  }}>
                    {t('receiptVouchers.add.to', { defaultValue: 'To' })}:
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#000',
                    direction: isRTL ? 'rtl' : 'ltr',
                    padding: '0 10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                  }}>
                    {accountCode ? `${accountCode} - ` : ''}{accountName || '—'}
                  </div>
                </div>

                {voucher.notes && (
                  <div style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                    <div style={{
                      width: '30%',
                      borderLeft: '1px solid #000',
                      backgroundColor: 'rgba(227, 242, 253, 0.4)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      fontWeight: '700',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      color: '#1976d2',
                      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      padding: '0 10px'
                    }}>
                      {t('receiptVouchers.add.notes', { defaultValue: 'Notes' })}:
                    </div>
                    <div style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      textAlign: isRTL ? 'right' : 'left',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: '#000',
                      direction: isRTL ? 'rtl' : 'ltr',
                      padding: '0 10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                    }}>
                      {voucher.notes}
                    </div>
                  </div>
                )}

                {details.length > 0 && (
                  <div style={{ borderBottom: '1px solid #000' }}>
                    <div style={{
                      width: '100%',
                      backgroundColor: 'rgba(227, 242, 253, 0.4)',
                      padding: '8px 10px',
                      fontWeight: '700',
                      fontSize: '14px',
                      direction: isRTL ? 'rtl' : 'ltr',
                      color: '#1976d2',
                      fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      borderBottom: '1px solid #000'
                    }}>
                      {isRTL ? `:${t('receiptVouchers.add.from', { defaultValue: 'From' })}` : `${t('receiptVouchers.add.from', { defaultValue: 'From' })}:`}
                    </div>
                    {details.map((detail, index) => (
                      <div key={detail.id || index} style={{
                        display: 'flex',
                        borderBottom: index < details.length - 1 ? '1px solid #ccc' : 'none',
                        minHeight: '35px',
                        backgroundColor: index % 2 === 0 ? 'rgba(255, 255, 255, 0.5)' : 'rgba(255, 255, 255, 0.3)'
                      }}>
                        <div style={{
                          width: '10%',
                          borderLeft: '1px solid #000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#000',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0 8px',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                        }}>
                          {index + 1}
                        </div>
                        <div style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: isRTL ? 'right' : 'left',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#000',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0 10px',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                        }}>
                          {detail.account ? (isRTL ? (detail.account.name_ar || detail.account.name) : (detail.account.name_en || detail.account.name_ar || detail.account.name)) : '—'}
                        </div>
                        <div style={{
                          width: '20%',
                          borderLeft: '1px solid #000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#000',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0 8px',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                        }}>
                          {detail.account?.code || '—'}
                        </div>
                        <div style={{
                          width: '25%',
                          borderLeft: '1px solid #000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'flex-start',
                          textAlign: isRTL ? 'right' : 'left',
                          fontSize: '13px',
                          fontWeight: '500',
                          color: '#000',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0 10px',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                        }}>
                          {detail.description || '—'}
                        </div>
                        <div style={{
                          width: '20%',
                          borderLeft: '1px solid #000',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '13px',
                          fontWeight: '600',
                          color: '#000',
                          direction: isRTL ? 'rtl' : 'ltr',
                          padding: '0 8px',
                          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                        }}>
                          {formatCurrency(detail.amount)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'flex', borderTop: '2px solid #000', minHeight: '45px', backgroundColor: 'rgba(227, 242, 253, 0.6)' }}>
                  <div style={{
                    width: '30%',
                    borderLeft: '1px solid #000',
                    backgroundColor: 'rgba(227, 242, 253, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontWeight: '700',
                    fontSize: '16px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    color: '#1976d2',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    padding: '0 10px'
                  }}>
                    {isRTL ? `:${t('receiptVouchers.add.totalAmount', { defaultValue: 'Total Amount' })}` : `${t('receiptVouchers.add.totalAmount', { defaultValue: 'Total Amount' })}:`}
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#000',
                    direction: isRTL ? 'rtl' : 'ltr',
                    padding: '0 10px',
                    backgroundColor: 'rgba(227, 242, 253, 0.6)',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                  }}>
                    {formatCurrency(totalAmount)}
                  </div>
                </div>

                <div style={{ display: 'flex', borderTop: '1px solid #000', minHeight: '38px' }}>
                  <div style={{
                    width: '30%',
                    borderLeft: '1px solid #000',
                    backgroundColor: 'rgba(227, 242, 253, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontWeight: '700',
                    fontSize: '14px',
                    direction: isRTL ? 'rtl' : 'ltr',
                    color: '#1976d2',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    padding: '0 10px'
                  }}>
                    {isRTL ? `:${t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })}` : `${t('receiptVouchers.tableHeaders.status', { defaultValue: 'Status' })}:`}
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: isRTL ? 'right' : 'left',
                    fontSize: '15px',
                    fontWeight: '500',
                    color: '#000',
                    direction: isRTL ? 'rtl' : 'ltr',
                    padding: '0 10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                    fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif'
                  }}>
                    {statusLabel(voucher.status)}
                  </div>
                </div>
              </div>
            </div>
            <div style={{
              position: 'absolute',
              bottom: '10px',
              left: '18px',
              right: '18px',
              flexShrink: 0
            }}>
              <img
                src={`${publicUrl}/FooterAce.png`}
                alt="Footer"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  borderTop: '4px double #000',
                  paddingTop: '8px',
                  flexShrink: 0
                }}
                crossOrigin="anonymous"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


/* Dropdown Component */
const DropdownWithSearch = ({ isOpen, onToggle, searchTerm, onSearchChange, searchInputRef, items, pagination, currentPage, onPageChange, onSelect, displayValue, placeholder, label, renderItem }) => {
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';

  const renderDropdownItemLabel = (item, codeField, nameArField, nameEnField) => {
    const code = item[codeField] || '';
    const name = isRTL
      ? (item[nameArField] || item[nameEnField] || '')
      : (item[nameEnField] || item[nameArField] || '');
    return `${code ? `${code} - ` : ''}${name}`;
  };

  const internalInputRef = useRef(null);
  const inputRef = searchInputRef || internalInputRef;

  useEffect(() => {
    if (isOpen && inputRef?.current) {
      const timer = setTimeout(() => {
        inputRef.current?.focus({ preventScroll: true });
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, inputRef]);

  if (!isOpen) return null;

  return (
    <div style={{ position: 'absolute', zIndex: 10, top: '100%', left: 0, right: 0, background: '#1a1f2e', border: '1px solid #333', borderRadius: 8, marginTop: 4, maxHeight: 400, overflowY: 'auto' }}>
      <div style={{ padding: 8 }}>
        <input
          ref={inputRef}
          type="text"
          placeholder={`${t('quotations.add.search', { defaultValue: 'ابحث عن' })} ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              inputRef.current?.focus({ preventScroll: true });
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          style={{ width: '100%', padding: 8, backgroundColor: '#14263b', border: '1px solid #444', borderRadius: 6, color: 'white', fontSize: 14 }}
        />
      </div>
      {pagination && pagination.last_page >= 1 && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('common.pagination.page', { defaultValue: 'الصفحة' })} {pagination.current_page} {t('common.pagination.of', { defaultValue: 'من' })} {pagination.last_page}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div>
        {(Array.isArray(items) ? items : []).map((item) => (
          <div
            key={item.id}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect(item.id);
            }}
            style={{
              padding: '12px 15px',
              cursor: 'pointer',
              borderTop: '1px solid #333',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'white',
              fontSize: '14px',
              transition: 'background-color 0.2s',
              direction: isRTL ? 'rtl' : 'ltr',
              textAlign: isRTL ? 'right' : 'left'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14263b'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span style={{ color: 'white' }}>
              {renderItem ? renderItem(item) : renderDropdownItemLabel(item, 'code', 'name_ar', 'name_en')}
            </span>
          </div>
        ))}
        {(!Array.isArray(items) || items.length === 0) && <div style={{ padding: 12, textAlign: 'center', color: '#bbb', direction: isRTL ? 'rtl' : 'ltr' }}>{t('quotations.add.noResults', { defaultValue: 'لا توجد نتائج' })}</div>}
      </div>
    </div>
  );
};

export default AddReceiptVoucher;